import { Button, Card, Container, Group, SimpleGrid, Stack, Text, ThemeIcon, Title } from '@mantine/core'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuAnchor, LuCalendarCheck, LuCircleCheck, LuShip } from 'react-icons/lu'
import { BookingTable } from '@/frontend/components/marina/BookingTable'
import { PillNav } from '@/frontend/components/marina/PillNav'
import { useCanWrite } from '@/frontend/hooks/useAuth'
import { type BookingStatus, useBookingStatusCounts, useBookings } from '@/frontend/hooks/useBookings'

type TabValue = 'ALL' | Extract<BookingStatus, 'RESERVED' | 'CHECKED_IN' | 'CHECKED_OUT' | 'CANCELLED'>

const VALID: TabValue[] = ['ALL', 'RESERVED', 'CHECKED_IN', 'CHECKED_OUT', 'CANCELLED']

export const Route = createFileRoute('/dashboard/reservations/bookings')({
  validateSearch: (s: Record<string, unknown>): { tab?: TabValue } => ({
    tab: VALID.includes(s.tab as TabValue) ? (s.tab as TabValue) : undefined,
  }),
  component: BookingsList,
})

function BookingsList() {
  const { t } = useTranslation()
  const navigate = useNavigate({ from: Route.fullPath })
  const canWrite = useCanWrite()
  const { tab: rawTab } = Route.useSearch()
  const tab = rawTab ?? 'ALL'
  const setTab = (v: TabValue) => navigate({ search: () => ({ tab: v }), replace: true })
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const pageSize = 15

  const { data, isLoading } = useBookings({
    q: query,
    view: 'bookings',
    status: tab === 'ALL' ? undefined : tab,
    page,
    pageSize,
  })
  const { data: counts } = useBookingStatusCounts()

  const n = (s: string) => counts?.counts[s] ?? 0
  const totalActive = n('RESERVED') + n('CHECKED_IN')

  const emptyLabel =
    tab === 'RESERVED'
      ? 'Tidak ada booking yang dipesan'
      : tab === 'CHECKED_IN'
        ? 'Tidak ada tamu yang sedang berlabuh'
        : tab === 'CHECKED_OUT'
          ? 'Belum ada tamu yang telah check-out'
          : tab === 'CANCELLED'
            ? 'Tidak ada booking yang dibatalkan'
            : 'Belum ada booking aktif'

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        {/* Header */}
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="sm" align="center">
            <ThemeIcon variant="light" color="laut" size="lg" radius="md">
              <LuCalendarCheck size={18} />
            </ThemeIcon>
            <div>
              <Title order={2} ff="var(--font-display)" fw={600}>
                {t('page.reservations.bookings.title')}
              </Title>
              <Text size="xs" c="dimmed">
                {t('page.reservations.bookings.subtitle')}
              </Text>
            </div>
          </Group>
          {canWrite && (
            <Button color="karang" size="sm" onClick={() => navigate({ to: '/dashboard/bookings/new' as any })}>
              {t('page.reservations.bookings.btn_new')}
            </Button>
          )}
        </Group>

        {/* Stats strip */}
        {!isLoading && totalActive > 0 && (
          <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="sm">
            <Card radius="md" padding="sm" px="md" style={{ cursor: 'pointer' }} onClick={() => setTab('CHECKED_IN')}>
              <Group gap="xs" wrap="nowrap">
                <ThemeIcon variant="light" color="laut" size="sm" radius="sm">
                  <LuShip size={13} />
                </ThemeIcon>
                <Text size="xs" c="dimmed">
                  {t('page.reservations.bookings.stat_in_marina')}
                </Text>
                <Text size="xs" fw={700} ml="auto" c={n('CHECKED_IN') > 0 ? 'laut.7' : undefined}>
                  {n('CHECKED_IN')}
                </Text>
              </Group>
            </Card>
            <Card radius="md" padding="sm" px="md" style={{ cursor: 'pointer' }} onClick={() => setTab('RESERVED')}>
              <Group gap="xs" wrap="nowrap">
                <ThemeIcon variant="light" color="blue" size="sm" radius="sm">
                  <LuAnchor size={13} />
                </ThemeIcon>
                <Text size="xs" c="dimmed">
                  {t('page.reservations.bookings.stat_reserved')}
                </Text>
                <Text size="xs" fw={700} ml="auto">
                  {n('RESERVED')}
                </Text>
              </Group>
            </Card>
            <Card radius="md" padding="sm" px="md">
              <Group gap="xs" wrap="nowrap">
                <ThemeIcon variant="light" color="teal" size="sm" radius="sm">
                  <LuCircleCheck size={13} />
                </ThemeIcon>
                <Text size="xs" c="dimmed">
                  {t('page.reservations.bookings.stat_completed')}
                </Text>
                <Text size="xs" fw={700} ml="auto">
                  {n('CHECKED_OUT')}
                </Text>
              </Group>
            </Card>
          </SimpleGrid>
        )}

        {/* Filter + search */}
        <Group justify="space-between" wrap="wrap" gap="sm">
          <PillNav
            value={tab}
            onChange={(v) => {
              setTab(v)
              setPage(1)
            }}
            items={[
              {
                value: 'ALL',
                label: t('page.reservations.bookings.tab_all'),
                count: n('RESERVED') + n('CHECKED_IN') + n('CHECKED_OUT') + n('CANCELLED') || undefined,
                countColor: 'gray',
              },
              {
                value: 'RESERVED',
                label: t('page.reservations.bookings.tab_reserved'),
                count: n('RESERVED'),
                countColor: 'blue',
              },
              {
                value: 'CHECKED_IN',
                label: t('page.reservations.bookings.tab_in'),
                count: n('CHECKED_IN'),
                countColor: 'laut',
              },
              {
                value: 'CHECKED_OUT',
                label: t('page.reservations.bookings.tab_out'),
                count: n('CHECKED_OUT'),
                countColor: 'teal',
              },
              {
                value: 'CANCELLED',
                label: t('page.reservations.bookings.tab_cancelled'),
                count: n('CANCELLED'),
                countColor: 'red',
              },
            ]}
          />
          <input
            type="search"
            value={search}
            placeholder={t('page.reservations.bookings.placeholder')}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setQuery(search)
                setPage(1)
              }
            }}
            style={{
              fontSize: '0.8rem',
              padding: '6px 12px',
              borderRadius: 8,
              border: '1px solid var(--mantine-color-default-border)',
              background: 'var(--app-card-bg)',
              color: 'inherit',
              width: 220,
              outline: 'none',
            }}
          />
        </Group>

        <BookingTable
          items={data?.items ?? []}
          total={data?.total ?? 0}
          isLoading={isLoading}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          emptyLabel={emptyLabel}
          highlightCheckedIn
        />
      </Stack>
    </Container>
  )
}
